import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { cn } from '@/utils/classname';

interface PasswordFieldProps {
  password: string;
  isRevealed: boolean;
  onToggle: () => void;
  label?: string;
  className?: string;
  size?: 'sm' | 'md';
}

/**
 * PasswordField - Reusable component for displaying passwords with show/hide toggle
 *
 * Usage:
 * ```tsx
 * const [revealedPasswords, setRevealedPasswords] = useState<Record<string, boolean>>({});
 *
 * const togglePassword = (id: string) => {
 *   setRevealedPasswords(prev => ({ ...prev, [id]: !prev[id] }));
 * };
 *
 * <PasswordField
 *   password="secret123"
 *   isRevealed={revealedPasswords['user1'] || false}
 *   onToggle={() => togglePassword('user1')}
 *   label="Password"
 * />
 * ```
 */
export function PasswordField({
  password,
  isRevealed,
  onToggle,
  label,
  className,
  size = 'md',
}: PasswordFieldProps) {
  const displayValue = isRevealed ? password || 'N/A' : '••••••••';
  const iconSize = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {label && <span className="text-chariot-text-primary">{label}:</span>}
      <span className="text-chariot-text-primary">{displayValue}</span>
      <button
        type="button"
        onClick={e => {
          e.stopPropagation();
          onToggle();
        }}
        className="text-chariot-text-secondary hover:text-chariot-text-primary"
        aria-label={isRevealed ? 'Hide password' : 'Show password'}
      >
        {isRevealed ? (
          <EyeSlashIcon className={iconSize} />
        ) : (
          <EyeIcon className={iconSize} />
        )}
      </button>
    </div>
  );
}
